import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Row,
  Col
} from 'reactstrap';
import Swal from 'sweetalert2';
import Header from 'components/Headers/Header';
import {
  fetchExamAttempt,
  saveExamProgress,
  submitExam,
  clearCurrentAttempt
} from '../../../redux/QuestionBank/FullLengthExamAction';
import { draftRawToHtml } from '../../../util/draftToHtml';
import { getSpecialty } from '../../../config/specialtyConfig';
import QuestionFeedbackModal from '../../../components/QuestionBank/QuestionFeedbackModal';
import TutoredModeAIChat from '../../../components/QuestionBank/TutoredModeAIChat';

const TakeFullLengthExam = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { attemptId } = useParams();
  const isReviewMode = location.state?.reviewMode || false;

  const fullLengthExamsState = useSelector(state => state.fullLengthExams);
  const currentAttempt = fullLengthExamsState?.currentAttempt;
  const attemptLoading = fullLengthExamsState?.attemptLoading;
  const submitLoading = fullLengthExamsState?.submitLoading;
  const error = fullLengthExamsState?.error;
  const userName = useSelector(state => state.loginAuth?.userDetails?.name);
  const activeSpecialty = useSelector(state => state.specialty?.activeSpecialty || 'pmr');
  const specialtyName = getSpecialty(activeSpecialty).name;

  // Test state
  const [testQuestions, setTestQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [questionTimeTracking, setQuestionTimeTracking] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  // PM&R Review state
  const [showPmrReview, setShowPmrReview] = useState(false);
  const [hasSeenPmrReview, setHasSeenPmrReview] = useState(false);
  const [pmrInstructionsOpen, setPmrInstructionsOpen] = useState(true);
  const [pmrQuestionsOpen, setPmrQuestionsOpen] = useState(true);

  // Section-aware state
  const [hasSections, setHasSections] = useState(false);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSectionQuestionIndex, setCurrentSectionQuestionIndex] = useState(0);
  const [sectionTimeRemaining, setSectionTimeRemaining] = useState(null);
  const [showBreakScreen, setShowBreakScreen] = useState(false);
  const [breakState, setBreakState] = useState(null);

  // UI state
  const [isTimeWarningShown, setIsTimeWarningShown] = useState(false);
  const [isSectionTimeWarningShown, setIsSectionTimeWarningShown] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [fontSizePercent, setFontSizePercent] = useState(120);

  // Refs for timer and auto-save
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const currentQuestionStartTimeRef = useRef(null);
  const testModalRef = useRef(null);
  const examContentRef = useRef(null);
  const saveStatusTimeoutRef = useRef(null);

  // Refs for latest state values
  const selectedAnswersRef = useRef(selectedAnswers);
  const questionTimeTrackingRef = useRef(questionTimeTracking);
  const currentQuestionIndexRef = useRef(currentQuestionIndex);
  const markedQuestionsRef = useRef(markedQuestions);
  const timeRemainingRef = useRef(timeRemaining);
  const testQuestionsRef = useRef(testQuestions);
  const sectionsRef = useRef(sections);
  const currentSectionIndexRef = useRef(currentSectionIndex);
  const currentSectionQuestionIndexRef = useRef(currentSectionQuestionIndex);
  const sectionTimeRemainingRef = useRef(sectionTimeRemaining);
  const hasSectionsRef = useRef(hasSections);
  const breakTimerRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { selectedAnswersRef.current = selectedAnswers; }, [selectedAnswers]);
  useEffect(() => { questionTimeTrackingRef.current = questionTimeTracking; }, [questionTimeTracking]);
  useEffect(() => { currentQuestionIndexRef.current = currentQuestionIndex; }, [currentQuestionIndex]);
  useEffect(() => { markedQuestionsRef.current = markedQuestions; }, [markedQuestions]);
  useEffect(() => { timeRemainingRef.current = timeRemaining; }, [timeRemaining]);
  useEffect(() => { testQuestionsRef.current = testQuestions; }, [testQuestions]);
  useEffect(() => { sectionsRef.current = sections; }, [sections]);
  useEffect(() => { currentSectionIndexRef.current = currentSectionIndex; }, [currentSectionIndex]);
  useEffect(() => { currentSectionQuestionIndexRef.current = currentSectionQuestionIndex; }, [currentSectionQuestionIndex]);
  useEffect(() => { sectionTimeRemainingRef.current = sectionTimeRemaining; }, [sectionTimeRemaining]);
  useEffect(() => { hasSectionsRef.current = hasSections; }, [hasSections]);

  // Constants
  const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

  // Load exam attempt on mount
  useEffect(() => {
    dispatch(fetchExamAttempt(attemptId));

    return () => {
      dispatch(clearCurrentAttempt());
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
    };
  }, [dispatch, attemptId]);

  // Load attempt data when fetched
  useEffect(() => {
    if (!currentAttempt) {
      return;
    }

    if (isLoaded) {
      return;
    }

    // Check if exam is already completed
    if (currentAttempt.status === 'completed' && !isReviewMode) {
      history.push(`/client/question-bank/full-length-exam/${attemptId}/results`);
      return;
    }

    // Check if we have test questions
    if (!currentAttempt.testQuestions || currentAttempt.testQuestions.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No questions found in this exam. Please try again.',
        confirmButtonColor: '#1e6091'
      }).then(() => {
        history.push('/client/question-bank/full-length-exams');
      });
      return;
    }

    setTestQuestions(currentAttempt.testQuestions);
    setCurrentQuestionIndex(isReviewMode ? 0 : (currentAttempt.currentQuestionIndex || 0));
    setSelectedAnswers(currentAttempt.selectedAnswers || {});
    setMarkedQuestions(currentAttempt.markedQuestions || []);
    setTimeRemaining(currentAttempt.timeRemaining);
    setQuestionTimeTracking(currentAttempt.questionTimeTracking || {});

    // Load section state if applicable
    if (currentAttempt.hasSections && currentAttempt.sections) {
      setHasSections(true);
      setSections(currentAttempt.sections);
      setCurrentSectionIndex(currentAttempt.currentSectionIndex || 0);
      setCurrentSectionQuestionIndex(currentAttempt.currentSectionQuestionIndex || 0);

      const curSection = currentAttempt.sections[currentAttempt.currentSectionIndex || 0];
      setSectionTimeRemaining(curSection?.timeRemaining ?? curSection?.timeLimitSeconds);

      // Check if we were in a break state
      if (currentAttempt.breakState && currentAttempt.breakState.startedAt) {
        const elapsed = Math.floor((Date.now() - new Date(currentAttempt.breakState.startedAt).getTime()) / 1000);
        const remaining = Math.max(0, (currentAttempt.breakDurationSeconds || 0) - elapsed);
        if (remaining > 0) {
          setBreakState({ remaining, startedAt: currentAttempt.breakState.startedAt });
          setShowBreakScreen(true);
        } else {
          // Break expired while away — auto-start next section
          const nextIdx = (currentAttempt.currentSectionIndex || 0) + 1;
          if (nextIdx < currentAttempt.sections.length) {
            const updatedSections = [...currentAttempt.sections];
            updatedSections[nextIdx] = { ...updatedSections[nextIdx], status: 'in_progress', startedAt: new Date().toISOString() };
            setSections(updatedSections);
            setCurrentSectionIndex(nextIdx);
            setCurrentSectionQuestionIndex(0);
            setSectionTimeRemaining(updatedSections[nextIdx].timeLimitSeconds);
            const globalIdx = updatedSections.slice(0, nextIdx).reduce((sum, s) => sum + s.questionCount, 0);
            setCurrentQuestionIndex(globalIdx);
          }
        }
      }
    }

    Swal.close();
    setIsLoaded(true);
    setIsTestModalOpen(true);
    currentQuestionStartTimeRef.current = Date.now();
  }, [currentAttempt, attemptId, isLoaded, history]);

  // Timer countdown
  useEffect(() => {
    if (!isLoaded || !isTestModalOpen || showBreakScreen || isReviewMode) return;

    if (hasSections) {
      // Section timer
      if (sectionTimeRemaining === null) return;
      timerRef.current = setInterval(() => {
        setSectionTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSectionTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Legacy global timer
      if (timeRemaining === null) return;
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isLoaded, isTestModalOpen, hasSections, showBreakScreen]);

  // Auto-save interval
  useEffect(() => {
    if (!isLoaded || !attemptId || !isTestModalOpen || isReviewMode) return;

    autoSaveRef.current = setInterval(() => {
      handleAutoSave();
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [isLoaded, attemptId, isTestModalOpen]);

  // Time warning at 10 minutes (only if exam is longer than 10 minutes)
  useEffect(() => {
    // Only show warning if exam started with more than 10 minutes and now has 10 or less
    const examTimeLimit = currentAttempt?.timeLimit;
    if (
      timeRemaining !== null &&
      examTimeLimit > 600 && // Only for exams longer than 10 minutes
      timeRemaining <= 600 &&
      !isTimeWarningShown
    ) {
      setIsTimeWarningShown(true);
      Swal.fire({
        icon: 'warning',
        title: '10 Minutes Remaining',
        text: 'You have 10 minutes left to complete the exam.',
        confirmButtonColor: '#1e6091',
        timer: 5000
      });
    }
  }, [timeRemaining, isTimeWarningShown, currentAttempt?.timeLimit]);

  // Section time warning at 10 minutes
  useEffect(() => {
    if (
      hasSections &&
      sectionTimeRemaining !== null &&
      sections[currentSectionIndex]?.timeLimitSeconds > 600 &&
      sectionTimeRemaining <= 600 &&
      !isSectionTimeWarningShown
    ) {
      setIsSectionTimeWarningShown(true);
      Swal.fire({
        icon: 'warning',
        title: '10 Minutes Remaining',
        text: `You have 10 minutes left in ${sections[currentSectionIndex]?.name || 'this section'}.`,
        confirmButtonColor: '#1e6091',
        timer: 5000
      });
    }
  }, [sectionTimeRemaining, isSectionTimeWarningShown, hasSections, currentSectionIndex, sections]);

  // Break timer countdown
  useEffect(() => {
    if (!showBreakScreen || !breakState) return;

    breakTimerRef.current = setInterval(() => {
      setBreakState(prev => {
        if (!prev || prev.remaining <= 1) {
          clearInterval(breakTimerRef.current);
          // Break expired — auto-start next section
          const nextIdx = currentSectionIndexRef.current + 1;
          if (nextIdx < sectionsRef.current.length) {
            startNextSection([...sectionsRef.current], nextIdx);
          }
          return null;
        }
        return { ...prev, remaining: prev.remaining - 1 };
      });
    }, 1000);

    return () => {
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
    };
  }, [showBreakScreen]);

  // Beforeunload warning
  useEffect(() => {
    if (!isTestModalOpen || isReviewMode) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      handleAutoSave();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTestModalOpen, selectedAnswers, currentQuestionIndex, markedQuestions, questionTimeTracking]);

  // Get current question
  const currentQuestion = testQuestions[currentQuestionIndex];

  // Detect when content overflows and show scroll indicator
  useEffect(() => {
    const el = examContentRef.current;
    if (!el) return;
    const checkScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight;
      const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      setCanScrollDown(hasOverflow && !isNearBottom);
    };
    // Small delay to let content render
    const timer = setTimeout(checkScroll, 100);
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [currentQuestion, showBreakScreen, showPmrReview]);

  // Scroll exam content to top whenever the question changes
  useEffect(() => {
    examContentRef.current?.scrollTo({ top: 0 });
  }, [currentQuestionIndex]);

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update question time tracking
  const updateQuestionTime = () => {
    if (!currentQuestion || !currentQuestionStartTimeRef.current || isReviewMode) return;

    const timeSpent = Math.floor((Date.now() - currentQuestionStartTimeRef.current) / 1000);
    setQuestionTimeTracking(prev => ({
      ...prev,
      [currentQuestion.questionId]: (prev[currentQuestion.questionId] || 0) + timeSpent
    }));
  };

  // Handle answer selection (click again to deselect)
  const handleSelectAnswer = (answerId) => {
    if (!currentQuestion || isReviewMode) return;
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion.questionId]: prev[currentQuestion.questionId] === answerId ? null : answerId
    }));
  };

  // Handle mark question
  const handleMarkQuestion = () => {
    if (!currentQuestion) return;
    setMarkedQuestions(prev => {
      if (prev.includes(currentQuestion.questionId)) {
        return prev.filter(id => id !== currentQuestion.questionId);
      }
      return [...prev, currentQuestion.questionId];
    });
    // Auto-save after marking/unmarking question
    handleAutoSave();
  };

  // Get the global start index for the current section
  const getSectionGlobalStartIndex = (sectionIdx) => {
    if (!hasSections) return 0;
    return sections.slice(0, sectionIdx).reduce((sum, s) => sum + s.questionCount, 0);
  };

  // Navigate to previous question
  const handlePrevQuestion = () => {
    if (isReviewMode) {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      }
      return;
    }
    if (hasSections) {
      // Don't allow going before current section start
      if (currentSectionQuestionIndex > 0) {
        updateQuestionTime();
        setCurrentSectionQuestionIndex(prev => prev - 1);
        setCurrentQuestionIndex(prev => prev - 1);
        currentQuestionStartTimeRef.current = Date.now();
        handleAutoSave(true);
      }
      return;
    }
    if (currentQuestionIndex > 0) {
      updateQuestionTime();
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      currentQuestionStartTimeRef.current = Date.now();
      handleAutoSave(true);
    }
  };

  // Navigate to next question
  const handleNextQuestion = () => {
    if (isReviewMode) {
      if (currentQuestionIndex < testQuestions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
      return;
    }
    if (hasSections) {
      const currentSection = sections[currentSectionIndex];
      if (currentSectionQuestionIndex < currentSection.questionCount - 1) {
        updateQuestionTime();
        setCurrentSectionQuestionIndex(prev => prev + 1);
        setCurrentQuestionIndex(prev => prev + 1);
        currentQuestionStartTimeRef.current = Date.now();
        handleAutoSave(true);
      }
      // At end of section, Next does nothing — user must use review screen to end section
      return;
    }
    if (currentQuestionIndex < testQuestions.length - 1) {
      updateQuestionTime();
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      currentQuestionStartTimeRef.current = Date.now();
      handleAutoSave(true);
    }
  };

  // Navigate to specific question (used from review screen)
  const navigateToQuestion = (index) => {
    if (isReviewMode) {
      if (index < 0 || index >= testQuestions.length) return;
      setCurrentQuestionIndex(index);
      setShowPmrReview(false);
      return;
    }
    if (hasSections) {
      // Only allow navigation within current section
      const sectionStart = getSectionGlobalStartIndex(currentSectionIndex);
      const sectionEnd = sectionStart + sections[currentSectionIndex].questionCount;
      if (index < sectionStart || index >= sectionEnd) return;
      updateQuestionTime();
      setCurrentQuestionIndex(index);
      setCurrentSectionQuestionIndex(index - sectionStart);
      currentQuestionStartTimeRef.current = Date.now();
      setShowPmrReview(false);
      handleAutoSave(true);
      return;
    }
    if (index < 0 || index >= testQuestions.length) return;
    updateQuestionTime();
    setCurrentQuestionIndex(index);
    currentQuestionStartTimeRef.current = Date.now();
    setShowPmrReview(false);
    handleAutoSave(true);
  };

  // Handle auto-save
  // skipTimeCalculation: set to true when updateQuestionTime() was already called before this
  const handleAutoSave = async (skipTimeCalculation = false) => {
    if (!attemptId || submitLoading) return;

    // Clear any existing "saved" status timeout
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
    }

    setSaveStatus('saving');

    let updatedTimeTracking = { ...questionTimeTrackingRef.current };

    // Only calculate time if not already done by updateQuestionTime
    if (!skipTimeCalculation && currentQuestionStartTimeRef.current && testQuestionsRef.current[currentQuestionIndexRef.current]) {
      const questionId = testQuestionsRef.current[currentQuestionIndexRef.current].questionId;
      const timeSpent = Math.floor((Date.now() - currentQuestionStartTimeRef.current) / 1000);
      updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
    }

    const progressData = {
      currentQuestionIndex: currentQuestionIndexRef.current,
      selectedAnswers: selectedAnswersRef.current,
      markedQuestions: markedQuestionsRef.current,
      timeRemaining: hasSectionsRef.current ? sectionTimeRemainingRef.current : timeRemainingRef.current,
      questionTimeTracking: updatedTimeTracking,
      ...(hasSectionsRef.current && {
        hasSections: true,
        currentSectionIndex: currentSectionIndexRef.current,
        currentSectionQuestionIndex: currentSectionQuestionIndexRef.current,
        sections: sectionsRef.current.map((s, idx) =>
          idx === currentSectionIndexRef.current
            ? { ...s, timeRemaining: sectionTimeRemainingRef.current }
            : s
        ),
        breakState: breakState || null,
      }),
    };

    const result = await dispatch(saveExamProgress(attemptId, progressData));

    if (result && result.success !== false) {
      setSaveStatus('saved');
      saveStatusTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } else {
      setSaveStatus('error');
    }
  };

  // Handle time up
  const handleTimeUp = async () => {
    await Swal.fire({
      icon: 'info',
      title: 'Time is Up!',
      text: 'Your exam will now be submitted.',
      confirmButtonColor: '#1e6091',
      allowOutsideClick: false
    });

    handleSubmitExam(true);
  };

  // Handle pause exam - save and exit with warning
  const handlePauseExam = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Pause Exam?',
      html: `<p style="margin-bottom:8px">This practice exam is designed to simulate real test conditions.</p>
             <p style="margin-bottom:8px">Pausing may affect your ability to accurately assess your readiness.</p>
             <p style="margin-bottom:0">Your progress will be saved and you can resume anytime.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Save & Pause',
      cancelButtonText: 'Continue Exam',
      confirmButtonColor: '#1e6091',
      cancelButtonColor: '#1e6091',
      iconColor: '#1e6091',
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Saving Progress...',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => { Swal.showLoading(); }
        });

        await handleAutoSave();

        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Your progress has been saved. You can resume this exam later.',
          confirmButtonColor: '#1e6091',
          timer: 1500,
          showConfirmButton: false
        });

        setTimeout(() => {
          setIsTestModalOpen(false);
          history.push('/client/question-bank/full-length-exams');
        }, 1500);
      }
    });
  };

  // Handle end exam - go to review screen
  const handleEndExam = () => {
    setShowPmrReview(true);
    setHasSeenPmrReview(true);
  };

  // Handle section time up
  const handleSectionTimeUp = async () => {
    const updatedSections = [...sectionsRef.current];
    updatedSections[currentSectionIndexRef.current] = {
      ...updatedSections[currentSectionIndexRef.current],
      status: 'completed',
      timeRemaining: 0,
      completedAt: new Date().toISOString(),
    };
    setSections(updatedSections);

    const nextIdx = currentSectionIndexRef.current + 1;

    if (nextIdx >= updatedSections.length) {
      await Swal.fire({
        icon: 'info',
        title: 'Time is Up!',
        text: 'All sections are complete. Your exam will now be submitted.',
        confirmButtonColor: '#1e6091',
        allowOutsideClick: false
      });
      handleSubmitExam(true);
    } else {
      await Swal.fire({
        icon: 'info',
        title: 'Section Complete',
        text: `Time is up for ${updatedSections[currentSectionIndexRef.current].name}. You may take a break before the next section.`,
        confirmButtonColor: '#1e6091',
        allowOutsideClick: false
      });
      transitionToBreakOrNextSection(updatedSections, nextIdx);
    }
  };

  // Handle student manually ending a section
  const handleEndSection = async () => {
    const currentSection = sections[currentSectionIndex];
    const sectionStart = getSectionGlobalStartIndex(currentSectionIndex);
    const sectionQuestions = testQuestions.slice(sectionStart, sectionStart + currentSection.questionCount);
    const unanswered = sectionQuestions.filter(q => !selectedAnswers[q.questionId]).length;

    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: `End ${currentSection.name}?`,
      html: `<p>Once you end this section, you <strong>cannot go back</strong> to these questions.</p>` +
        (unanswered > 0 ? `<p style="color: #dc3545;">${unanswered} question(s) unanswered in this section.</p>` : ''),
      showCancelButton: true,
      confirmButtonText: 'End Section',
      cancelButtonText: 'Continue Working',
      confirmButtonColor: '#1e6091'
    });

    if (!confirmResult.isConfirmed) return;

    updateQuestionTime();

    const updatedSections = [...sections];
    updatedSections[currentSectionIndex] = {
      ...updatedSections[currentSectionIndex],
      status: 'completed',
      timeRemaining: sectionTimeRemaining,
      completedAt: new Date().toISOString(),
    };
    setSections(updatedSections);

    const nextIdx = currentSectionIndex + 1;

    if (nextIdx >= updatedSections.length) {
      handleSubmitExam(false);
    } else {
      transitionToBreakOrNextSection(updatedSections, nextIdx);
    }
  };

  // Transition to break or next section
  const transitionToBreakOrNextSection = (updatedSections, nextIdx) => {
    const breakDuration = currentAttempt?.breakDurationSeconds || 0;

    if (breakDuration > 0) {
      setBreakState({ remaining: breakDuration, startedAt: new Date().toISOString() });
      setShowBreakScreen(true);
      handleAutoSave();
    } else {
      startNextSection(updatedSections, nextIdx);
    }
  };

  // Start the next section
  const startNextSection = (updatedSections, nextIdx) => {
    updatedSections[nextIdx] = {
      ...updatedSections[nextIdx],
      status: 'in_progress',
      startedAt: new Date().toISOString(),
    };
    setSections(updatedSections);
    setCurrentSectionIndex(nextIdx);
    setCurrentSectionQuestionIndex(0);
    setSectionTimeRemaining(updatedSections[nextIdx].timeLimitSeconds);
    setShowBreakScreen(false);
    setBreakState(null);
    setIsSectionTimeWarningShown(false);
    setShowPmrReview(false);
    setHasSeenPmrReview(false);
    currentQuestionStartTimeRef.current = Date.now();

    const globalIdx = updatedSections.slice(0, nextIdx).reduce((sum, s) => sum + s.questionCount, 0);
    setCurrentQuestionIndex(globalIdx);

    handleAutoSave();
  };

  // Handle submit exam
  const handleSubmitExam = async (isAutoSubmit = false) => {
    if (!isAutoSubmit) {
      const unansweredCount = testQuestions.length - Object.keys(selectedAnswers).length;

      if (unansweredCount > 0) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Unanswered Questions',
          text: `You have ${unansweredCount} unanswered question(s). Are you sure you want to submit?`,
          showCancelButton: true,
          confirmButtonText: 'Yes, Submit',
          cancelButtonText: 'Go Back',
          confirmButtonColor: '#1e6091'
        });

        if (!result.isConfirmed) {
          return;
        }
      }
    }

    // Stop auto-save and timer to prevent overwriting completed status
    // (placed after confirmation so intervals aren't killed if user cancels)
    if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current);
      autoSaveRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    updateQuestionTime();

    Swal.fire({
      title: 'Submitting Exam...',
      text: 'Calculating your results',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const finalData = {
      testQuestions,
      selectedAnswers,
      markedQuestions,
      timeRemaining: hasSections ? (sectionTimeRemaining || 0) : (timeRemaining || 0),
      questionTimeTracking,
      ...(hasSections && {
        hasSections: true,
        sections: sections.map((s, idx) =>
          idx === currentSectionIndex
            ? { ...s, timeRemaining: sectionTimeRemaining || 0 }
            : s
        ),
      }),
    };

    const result = await dispatch(submitExam(attemptId, finalData));

    if (result.success) {
      await Swal.fire({
        icon: 'success',
        title: 'Exam Submitted!',
        html: `
          <div>
            <h2 style="color: ${result.score >= 70 ? '#28a745' : result.score >= 50 ? '#ffc107' : '#dc3545'}">
              ${result.score}%
            </h2>
            <p>You answered ${result.correctCount} out of ${testQuestions.length} questions correctly.</p>
          </div>
        `,
        confirmButtonText: 'View Results',
        confirmButtonColor: '#1e6091'
      });

      setIsTestModalOpen(false);
      history.push(`/client/question-bank/full-length-exam/${attemptId}/results`);
    }
  };

  // Render question text
  const renderQuestionText = (questionText) => {
    if (!questionText) return 'Question text not available';

    let html = '';
    try {
      if (typeof questionText === 'string') {
        const parsed = JSON.parse(questionText);
        html = draftRawToHtml(parsed);
      } else {
        html = draftRawToHtml(questionText);
      }
    } catch {
      html = questionText || 'Unable to display question';
    }

    return html;
  };

  // Render answer text
  const renderAnswerText = (answer) => {
    if (!answer) return '';
    if (typeof answer.answerText === 'string') {
      try {
        const parsed = JSON.parse(answer.answerText);
        return draftRawToHtml(parsed);
      } catch {
        return answer.answerText;
      }
    }
    return draftRawToHtml(answer.answerText) || '';
  };

  // Render explanation text (for review mode)
  const renderExplanationText = (explanationText) => {
    if (!explanationText) return '';
    try {
      if (typeof explanationText === 'string') {
        const parsed = JSON.parse(explanationText);
        return draftRawToHtml(parsed);
      }
      return draftRawToHtml(explanationText);
    } catch {
      return explanationText;
    }
  };

  // Close review mode and go back to results
  const handleCloseReview = () => {
    setIsTestModalOpen(false);
    history.push(`/client/question-bank/full-length-exam/${attemptId}/results`);
  };

  // Error state
  if (error && !currentAttempt) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row className="justify-content-center">
            <Col lg="6" md="8">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-exclamation-circle text-danger mb-3" style={{ fontSize: '3rem' }}></i>
                  <h4>Error Loading Exam</h4>
                  <p className="text-muted mb-4">{error}</p>
                  <Button
                    color="primary"
                    onClick={() => history.push('/client/question-bank/full-length-exams')}
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Back to Exams
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  // Loading state - show while fetching attempt data
  // Only show loading if we don't have the attempt yet
  if (attemptLoading && !currentAttempt) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row className="justify-content-center">
            <Col lg="6" md="8">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                  <h4 className="mb-0">Loading exam...</h4>
                  <p className="text-muted mt-2 mb-0">Please wait while we fetch your exam data.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  // If we have currentAttempt but not loaded yet, show loading (transition state)
  if (currentAttempt && !isLoaded) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row className="justify-content-center">
            <Col lg="6" md="8">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                  <h4 className="mb-0">Preparing exam...</h4>
                  <p className="text-muted mt-2 mb-0">Setting up your test environment.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* This container shows if modal is closed for some reason */}
        {!isTestModalOpen && isLoaded && (
          <div className="text-center py-5">
            <p className="text-muted">Exam session ended.</p>
            <Button
              color="primary"
              onClick={() => history.push('/client/question-bank/full-length-exams')}
            >
              Back to Exams
            </Button>
          </div>
        )}
      </Container>

      {/* Full-screen Test Modal - Board Style Only */}
      <Modal
        isOpen={isTestModalOpen}
        className="modal-dialog-centered"
        style={{ maxWidth: '100vw', margin: 0, height: '100dvh' }}
        contentClassName="h-100"
        backdrop="static"
        keyboard={false}
      >
        <div ref={testModalRef} className="d-flex flex-column h-100" style={{ backgroundColor: '#ffffff' }}>
          {/* PM&R Header - Steel Blue */}
          <div className="d-flex justify-content-between align-items-center px-3" style={{ backgroundColor: '#1e6091', minHeight: '36px' }}>
            <div className="d-flex align-items-center">
              <span className="text-white" style={{ fontSize: '0.9rem' }}>
                {specialtyName} - {userName || 'Candidate Name'}
              </span>
              {/* Save Status Indicator */}
              {!isReviewMode && saveStatus !== 'idle' && (
                <span className="ml-3 d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                  {saveStatus === 'saving' && (
                    <>
                      <i className="fas fa-sync fa-spin mr-1" style={{ color: '#ffffff' }}></i>
                      <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Saving...</span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <i className="fas fa-check-circle mr-1" style={{ color: '#ffffff' }}></i>
                      <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Saved</span>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <i className="fas fa-exclamation-circle mr-1" style={{ color: '#dc3545' }}></i>
                      <span className="d-none d-sm-inline" style={{ color: '#dc3545' }}>Save failed</span>
                    </>
                  )}
                </span>
              )}
            </div>
            <div className="d-flex flex-column align-items-end text-white" style={{ fontSize: '0.8rem' }}>
              <span>
                {isReviewMode ? (
                  <><i className="far fa-eye mr-1"></i>Review Mode</>
                ) : (
                  <><i className="far fa-clock mr-1"></i>
                  {hasSections ? (
                    <>Section Time: {formatTime(sectionTimeRemaining)}</>
                  ) : (
                    <>Time Remaining {formatTime(timeRemaining)}</>
                  )}</>
                )}
              </span>
              <span>
                <i className="far fa-file-alt mr-1"></i>
                {isReviewMode ? (
                  <>{currentQuestionIndex + 1} of {testQuestions.length}</>
                ) : hasSections ? (
                  <>{currentSectionQuestionIndex + 1} of {sections[currentSectionIndex]?.questionCount} ({sections[currentSectionIndex]?.name})</>
                ) : (
                  <>{currentQuestionIndex + 1} of {testQuestions.length}</>
                )}
              </span>
            </div>
          </div>

          {/* PM&R Progress Bar - White */}
          <div style={{ position: 'relative', height: '1px', backgroundColor: '#ffffff', width: '100%' }}></div>

          {/* Flag for Review Row - Sky Blue Bar */}
          <div className="d-flex justify-content-between align-items-center px-3 py-1" style={{ backgroundColor: '#5BA4D9', minHeight: '28px' }}>
            {/* Exam Name - Left */}
            <span style={{ color: '#ffffff', fontSize: '0.85rem', fontWeight: '500' }}>
              {currentAttempt?.examName || 'Full-Length Exam'}
            </span>
            {/* Zoom & Flag for Review - Right (only show when not in review) */}
            {!showPmrReview && (
              <div className="d-flex align-items-center">
                <span className="d-flex align-items-center mr-3">
                  <i
                    className="fas fa-search-minus"
                    onClick={() => fontSizePercent > 50 && setFontSizePercent(prev => prev - 10)}
                    style={{ cursor: fontSizePercent <= 50 ? 'default' : 'pointer', color: fontSizePercent <= 50 ? '#a0c4e8' : '#ffffff', fontSize: '0.8rem' }}
                  ></i>
                  <span className="mx-1" style={{ color: '#ffffff', fontSize: '0.75rem', minWidth: '32px', textAlign: 'center' }}>{fontSizePercent}%</span>
                  <i
                    className="fas fa-search-plus"
                    onClick={() => fontSizePercent < 200 && setFontSizePercent(prev => prev + 10)}
                    style={{ cursor: fontSizePercent >= 200 ? 'default' : 'pointer', color: fontSizePercent >= 200 ? '#a0c4e8' : '#ffffff', fontSize: '0.8rem' }}
                  ></i>
                </span>
                <span
                  onClick={() => setIsFeedbackModalOpen(true)}
                  style={{ cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem', marginRight: '16px' }}
                  className="d-flex align-items-center"
                >
                  <i className="fas fa-comment-alt mr-1"></i>
                  Feedback
                </span>
                <span
                  onClick={handleMarkQuestion}
                  style={{ cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem' }}
                  className="d-flex align-items-center"
                >
                  <i className={`far fa-flag mr-1`} style={{ color: markedQuestions.includes(currentQuestion?.questionId) ? '#ffc107' : '#ffffff' }}></i>
                  Flag for Review
                </span>
              </div>
            )}
          </div>

          {/* PM&R Main Content Area - Clean White */}
          <div ref={examContentRef} className="flex-grow-1 overflow-auto exam-content-font" style={{ backgroundColor: '#ffffff', padding: '16px 24px', zoom: fontSizePercent / 100 }}>
            {showBreakScreen ? (
              /* Break Screen */
              <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center p-5">
                <i className="fas fa-coffee fa-4x text-muted mb-4"></i>
                <h3 className="mb-2">Break Time</h3>
                <p className="text-muted mb-3">
                  {sections[currentSectionIndex]?.name || 'Section'} is complete. Take a break before starting the next section.
                </p>
                <h1 className="mb-4" style={{ color: '#1e6091', fontFamily: 'monospace', fontSize: '3rem' }}>
                  {formatTime(breakState?.remaining || 0)}
                </h1>
                <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                  Break ends automatically when time runs out, or you can start the next section now.
                </p>
                {currentSectionIndex + 1 < sections.length && (
                  <button
                    onClick={() => {
                      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
                      const nextIdx = currentSectionIndex + 1;
                      startNextSection([...sections], nextIdx);
                    }}
                    style={{
                      backgroundColor: '#1e6091',
                      border: 'none',
                      color: '#ffffff',
                      padding: '12px 32px',
                      fontSize: '1rem',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-play mr-2"></i>
                    Start {sections[currentSectionIndex + 1]?.name || 'Next Section'} Now
                  </button>
                )}
              </div>
            ) : showPmrReview ? (
              /* PM&R Review Section */
              <>
                <h4 className="text-center mb-4" style={{ color: '#333', fontWeight: '700' }}>
                {isReviewMode ? 'Question Navigator' : (hasSections ? `${sections[currentSectionIndex]?.name || 'Section'} Review` : 'Exam Review')}
              </h4>

                {/* Instructions Section */}
                {!isReviewMode && (
                  <>
                    <div
                      style={{ backgroundColor: '#5BA4D9', color: '#ffffff', padding: '8px 12px', marginBottom: pmrInstructionsOpen ? '0' : '12px', cursor: 'pointer' }}
                      onClick={() => setPmrInstructionsOpen(!pmrInstructionsOpen)}
                    >
                      <i className={`fas fa-${pmrInstructionsOpen ? 'minus' : 'plus'}-square mr-2`}></i>
                      Instructions
                    </div>
                    {pmrInstructionsOpen && (
                      <div style={{ fontSize: '0.85rem', color: '#333', marginBottom: '20px', lineHeight: '1.6', padding: '12px', border: '1px solid #dee2e6', borderTop: 'none' }}>
                        <p className="mb-2">Below is a summary of your answers. You can review your questions in three (3) different ways.</p>
                        <p className="mb-2">The buttons in the lower right-hand corner correspond to these choices:</p>
                        <p className="mb-1">1. Review all of your questions and answers.</p>
                        <p className="mb-1">2. Review questions that are incomplete.</p>
                        <p className="mb-2">3. Review questions that are flagged for review. (Click the 'flag' icon to change the flag for review status.)</p>
                        <p className="mb-0">You may also click on a question number to link directly to its location in the exam.</p>
                      </div>
                    )}
                  </>
                )}

                {/* Questions Section */}
                <div
                  style={{ backgroundColor: '#5BA4D9', color: '#ffffff', padding: '8px 12px', marginBottom: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setPmrQuestionsOpen(!pmrQuestionsOpen)}
                >
                  <span><i className={`fas fa-${pmrQuestionsOpen ? 'minus' : 'plus'}-square mr-2`}></i>Questions</span>
                  <span>({(() => {
                    if (isReviewMode) {
                      return testQuestions.length;
                    }
                    if (hasSections) {
                      const start = getSectionGlobalStartIndex(currentSectionIndex);
                      const sectionQs = testQuestions.slice(start, start + sections[currentSectionIndex].questionCount);
                      return sectionQs.filter(q => !selectedAnswers[q.questionId]).length;
                    }
                    return testQuestions.filter(q => !selectedAnswers[q.questionId]).length;
                  })()} {isReviewMode ? 'Questions' : 'Unseen/Incomplete'})</span>
                </div>

                {/* Questions Grid */}
                {pmrQuestionsOpen && (
                  <div style={{ border: '1px solid #dee2e6', borderTop: 'none' }}>
                    <div className="row no-gutters">
                      {(() => {
                        const sectionStart = (isReviewMode || !hasSections) ? 0 : getSectionGlobalStartIndex(currentSectionIndex);
                        const sectionCount = (isReviewMode || !hasSections) ? testQuestions.length : sections[currentSectionIndex].questionCount;
                        return Array.from({ length: sectionCount }, (_, localIdx) => {
                          const qIndex = sectionStart + localIdx;
                          const q = testQuestions[qIndex];
                          const isAnswered = selectedAnswers[q.questionId] !== undefined;
                          const isMarked = markedQuestions.includes(q.questionId);
                          return (
                            <div key={q.questionId} className="col-12 col-sm-6 col-md-4"
                              style={{
                                padding: '8px 12px',
                                borderBottom: '1px solid #dee2e6',
                                borderRight: '1px solid #dee2e6',
                                cursor: 'pointer'
                              }}
                              onClick={() => navigateToQuestion(qIndex)}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <span>
                                  <i
                                    className={`${isMarked ? 'fas' : 'far'} fa-flag mr-2`}
                                    style={{ color: isMarked ? '#1e6091' : '#ccc', cursor: 'pointer' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (markedQuestions.includes(q.questionId)) {
                                        setMarkedQuestions(markedQuestions.filter(id => id !== q.questionId));
                                      } else {
                                        setMarkedQuestions([...markedQuestions, q.questionId]);
                                      }
                                    }}
                                  ></i>
                                  <span style={{ color: '#1e6091', cursor: 'pointer' }}>Question {localIdx + 1}</span>
                                </span>
                                {isReviewMode ? (() => {
                                  const userAns = selectedAnswers[q.questionId];
                                  if (!userAns) return <span style={{ color: '#ffc107', fontSize: '0.8rem' }}>Omitted</span>;
                                  const ansObj = q.answers?.find(a => a.answerId === userAns);
                                  return ansObj?.isCorrect
                                    ? <i className="fas fa-check" style={{ color: '#28a745', fontSize: '0.8rem' }}></i>
                                    : <i className="fas fa-times" style={{ color: '#dc3545', fontSize: '0.8rem' }}></i>;
                                })() : (
                                  !isAnswered && <span style={{ color: '#dc3545', fontSize: '0.8rem' }}>Incomplete</span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : currentQuestion ? (
              <>
                {/* Question Text - No header, just the question */}
                <div key={`q-text-${currentQuestion.questionId}`} style={{ marginBottom: '20px', color: '#333', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  <span
                    dangerouslySetInnerHTML={{ __html: renderQuestionText(currentQuestion.questionText) }}
                  />
                </div>

                {/* PM&R Answer Options - Normal Radio Buttons */}
                <div key={`q-answers-${currentQuestion.questionId}`}>
                  {currentQuestion.answers && currentQuestion.answers.map((answer, index) => {
                    const isSelected = selectedAnswers[currentQuestion.questionId] === answer.answerId;
                    const isCorrectAnswer = answer.isCorrect;

                    return (
                      <div
                        key={answer.answerId}
                        style={{
                          marginBottom: '10px',
                          cursor: isReviewMode ? 'default' : 'pointer',
                          ...(isReviewMode && isCorrectAnswer ? { backgroundColor: '#d4edda', borderRadius: '4px', padding: '4px 8px', marginLeft: '-8px', marginRight: '-8px' } : {}),
                          ...(isReviewMode && isSelected && !isCorrectAnswer ? { backgroundColor: '#f8d7da', borderRadius: '4px', padding: '4px 8px', marginLeft: '-8px', marginRight: '-8px' } : {})
                        }}
                        onClick={() => handleSelectAnswer(answer.answerId)}
                      >
                        <div className="d-flex align-items-start">
                          {isReviewMode && (
                            <span style={{ width: '20px', minWidth: '20px', marginTop: '3px', textAlign: 'center' }}>
                              {isCorrectAnswer && (
                                <i className="fas fa-check" style={{ color: '#28a745', fontSize: '0.8rem' }}></i>
                              )}
                              {isSelected && !isCorrectAnswer && (
                                <i className="fas fa-times" style={{ color: '#dc3545', fontSize: '0.8rem' }}></i>
                              )}
                            </span>
                          )}
                          <input
                            type="radio"
                            name={`question-${currentQuestion.questionId}`}
                            checked={isSelected}
                            readOnly
                            style={{ marginRight: '6px', marginTop: '4px', cursor: isReviewMode ? 'default' : 'pointer', pointerEvents: 'none' }}
                          />
                          <span style={{ fontSize: '0.9rem', color: '#333', minWidth: '24px' }}>
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <span style={{ fontSize: '0.9rem', color: '#333' }} dangerouslySetInnerHTML={{ __html: renderAnswerText(answer) }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Review Mode: Result Box */}
                {isReviewMode && (() => {
                  const userSelectedAnswerId = selectedAnswers[currentQuestion.questionId];
                  const wasOmitted = !userSelectedAnswerId;
                  const selectedAnswer = currentQuestion.answers?.find(a => a.answerId === userSelectedAnswerId);
                  const isCorrect = selectedAnswer?.isCorrect;
                  const correctIndex = currentQuestion.answers?.findIndex(a => a.isCorrect);

                  const statusColor = wasOmitted ? '#ffc107' : (isCorrect ? '#28a745' : '#dc3545');
                  const statusText = wasOmitted ? 'Omitted' : (isCorrect ? 'Correct' : 'Incorrect');
                  const statusIcon = wasOmitted ? 'fas fa-minus-circle' : (isCorrect ? 'fas fa-check-circle' : 'fas fa-times-circle');

                  const questionTime = questionTimeTracking[currentQuestion.questionId] || 0;
                  let timeSpentFormatted = 'N/A';
                  if (questionTime > 0) {
                    const mins = Math.floor(questionTime / 60);
                    const secs = questionTime % 60;
                    timeSpentFormatted = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                  }

                  return (
                    <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#f8f9fa', borderLeft: `4px solid ${statusColor}` }}>
                      <div className="d-flex flex-wrap align-items-center" style={{ gap: '16px', fontSize: '0.85rem' }}>
                        <span style={{ color: statusColor, fontWeight: '600' }}>
                          <i className={`${statusIcon} mr-1`}></i>{statusText}
                        </span>
                        <span style={{ color: '#555' }}>
                          <strong>Correct Answer:</strong> {correctIndex >= 0 ? String.fromCharCode(65 + correctIndex) : 'N/A'}
                        </span>
                        <span style={{ color: '#555' }}>
                          <strong>Time Spent:</strong> {timeSpentFormatted}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Review Mode: Explanation */}
                {isReviewMode && currentQuestion.explanation && (
                  <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#e8f4f8', borderLeft: '4px solid #1e6091' }}>
                    <h6 className="mb-2" style={{ color: '#1e6091', fontWeight: '600', fontSize: '0.85rem' }}>
                      <i className="fas fa-lightbulb mr-2"></i>Explanation
                    </h6>
                    <div key={`expl-${currentQuestion.questionId}`} style={{ fontSize: '0.85rem', color: '#333', lineHeight: '1.6' }}
                      dangerouslySetInnerHTML={{ __html: renderExplanationText(currentQuestion.explanation) }}
                    />
                  </div>
                )}

                {/* Review Mode: AI Tutor Chat */}
                {isReviewMode && (
                  <TutoredModeAIChat
                    key={currentQuestion.questionId}
                    questionText={currentQuestion.questionText}
                    explanationText={currentQuestion.explanation}
                    answers={currentQuestion.answers}
                    correctAnswerId={currentQuestion.answers?.find(a => a.isCorrect)?.answerId}
                    userAnswerId={selectedAnswers[currentQuestion.questionId]}
                    testId={attemptId}
                    questionId={currentQuestion.questionId}
                    variant="pmr"
                  />
                )}
              </>
            ) : (
              <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
                <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                <p className="mb-0">Loading...</p>
              </div>
            )}
            {/* Scroll indicator - shows when answer choices are below the fold */}
            <div style={{
              position: 'sticky',
              bottom: 0,
              textAlign: 'center',
              padding: '6px 0 2px',
              opacity: canScrollDown ? 1 : 0,
              transition: 'opacity 0.3s',
              pointerEvents: 'none'
            }}>
              <i className="fas fa-angle-double-down scroll-down-indicator" style={{ fontSize: '1.3rem', color: '#adb5bd' }}></i>
            </div>
          </div>

          {/* PM&R Bottom Navigation - Dark Blue */}
          <div className="d-flex justify-content-between align-items-center px-3 flex-wrap" style={{ backgroundColor: '#1e6091', minHeight: '50px', gap: '6px', padding: '6px 16px' }}>
            {showBreakScreen ? (
              <div></div>
            ) : isReviewMode && showPmrReview ? (
              <>
                {/* Review Mode Navigator - Left: Close */}
                <div>
                  <button
                    onClick={handleCloseReview}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-times mr-1"></i>
                    Close Review
                  </button>
                </div>
                {/* Review Mode Navigator - Right: Review All / Flagged */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  <button
                    onClick={() => {
                      setShowPmrReview(false);
                      setCurrentQuestionIndex(0);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-check-double mr-1"></i>
                    Review All
                  </button>
                  <button
                    onClick={() => {
                      let flaggedIndex = -1;
                      for (let i = 0; i < testQuestions.length; i++) {
                        if (markedQuestions.includes(testQuestions[i].questionId)) {
                          flaggedIndex = i;
                          break;
                        }
                      }
                      if (flaggedIndex >= 0) {
                        navigateToQuestion(flaggedIndex);
                      } else {
                        Swal.fire({
                          icon: 'info',
                          title: 'No Flagged Questions',
                          text: 'You have not flagged any questions for review.',
                          confirmButtonColor: '#1e6091'
                        });
                      }
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-flag mr-1"></i>
                    <span className="d-none d-sm-inline">Review </span>Flagged
                  </button>
                </div>
              </>
            ) : isReviewMode ? (
              <>
                {/* Review Mode - Left: Close + Navigator */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={handleCloseReview}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-times mr-1"></i>
                    Close
                  </button>
                  <button
                    onClick={() => setShowPmrReview(true)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-th mr-1"></i>
                    Navigator
                  </button>
                </div>
                {/* Review Mode - Right: Previous / Next */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={handlePrevQuestion}
                    disabled={currentQuestionIndex === 0}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                      opacity: currentQuestionIndex === 0 ? 0.5 : 1
                    }}
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Previous
                  </button>
                  <button
                    onClick={() => {
                      if (currentQuestionIndex === testQuestions.length - 1) {
                        setShowPmrReview(true);
                      } else {
                        handleNextQuestion();
                      }
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              </>
            ) : showPmrReview ? (
              <>
                {/* Left - End Section / Submit Exam */}
                <div>
                  {hasSections && currentSectionIndex < sections.length - 1 ? (
                    <button
                      onClick={handleEndSection}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #ffffff',
                        color: '#ffffff',
                        padding: '4px 12px',
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-forward mr-1"></i>
                      End {sections[currentSectionIndex]?.name || 'Section'}
                    </button>
                  ) : (
                    <button
                      onClick={hasSections ? handleEndSection : () => handleSubmitExam(false)}
                      disabled={submitLoading}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #ffffff',
                        color: '#ffffff',
                        padding: '4px 12px',
                        fontSize: '0.85rem',
                        cursor: submitLoading ? 'not-allowed' : 'pointer',
                        opacity: submitLoading ? 0.7 : 1
                      }}
                    >
                      {submitLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-1"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check mr-1"></i>
                          Submit Exam
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Right - Review buttons */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  <button
                    onClick={() => {
                      setShowPmrReview(false);
                      const startIdx = hasSections ? getSectionGlobalStartIndex(currentSectionIndex) : 0;
                      setCurrentQuestionIndex(startIdx);
                      if (hasSections) setCurrentSectionQuestionIndex(0);
                      currentQuestionStartTimeRef.current = Date.now();
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-check-double mr-1"></i>
                    Review All
                  </button>
                  <button
                    onClick={() => {
                      const sectionStart = hasSections ? getSectionGlobalStartIndex(currentSectionIndex) : 0;
                      const sectionEnd = hasSections ? sectionStart + sections[currentSectionIndex].questionCount : testQuestions.length;
                      let incompleteIndex = -1;
                      for (let i = sectionStart; i < sectionEnd; i++) {
                        if (!selectedAnswers[testQuestions[i].questionId]) {
                          incompleteIndex = i;
                          break;
                        }
                      }
                      if (incompleteIndex >= 0) {
                        navigateToQuestion(incompleteIndex);
                      } else {
                        Swal.fire({
                          icon: 'info',
                          title: 'All Complete',
                          text: 'All questions have been answered.',
                          confirmButtonColor: '#1e6091'
                        });
                      }
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-times mr-1"></i>
                    <span className="d-none d-sm-inline">Review </span>Incomplete
                  </button>
                  <button
                    onClick={() => {
                      const sectionStart = hasSections ? getSectionGlobalStartIndex(currentSectionIndex) : 0;
                      const sectionEnd = hasSections ? sectionStart + sections[currentSectionIndex].questionCount : testQuestions.length;
                      let flaggedIndex = -1;
                      for (let i = sectionStart; i < sectionEnd; i++) {
                        if (markedQuestions.includes(testQuestions[i].questionId)) {
                          flaggedIndex = i;
                          break;
                        }
                      }
                      if (flaggedIndex >= 0) {
                        navigateToQuestion(flaggedIndex);
                      } else {
                        Swal.fire({
                          icon: 'info',
                          title: 'No Flagged Questions',
                          text: 'You have not flagged any questions for review.',
                          confirmButtonColor: '#1e6091'
                        });
                      }
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-flag mr-1"></i>
                    <span className="d-none d-sm-inline">Review </span>Flagged
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Left side - Pause + Review Screen buttons */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={handlePauseExam}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-pause mr-1"></i>
                    Pause
                  </button>
                  {hasSeenPmrReview && (
                    <button
                      onClick={() => setShowPmrReview(true)}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #ffffff',
                        color: '#ffffff',
                        padding: '4px 12px',
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-sign-out-alt mr-1"></i>
                      Review Screen
                    </button>
                  )}
                </div>

                {/* Right - Previous and Next buttons */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={handlePrevQuestion}
                    disabled={hasSections ? currentSectionQuestionIndex === 0 : currentQuestionIndex === 0}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: (hasSections ? currentSectionQuestionIndex === 0 : currentQuestionIndex === 0) ? 'not-allowed' : 'pointer',
                      opacity: (hasSections ? currentSectionQuestionIndex === 0 : currentQuestionIndex === 0) ? 0.5 : 1
                    }}
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Previous
                  </button>
                  <button
                    onClick={() => {
                      if (hasSections) {
                        if (currentSectionQuestionIndex >= sections[currentSectionIndex].questionCount - 1) {
                          handleEndExam(); // Go to section review
                        } else {
                          handleNextQuestion();
                        }
                      } else if (currentQuestionIndex === testQuestions.length - 1) {
                        handleEndExam();
                      } else {
                        handleNextQuestion();
                      }
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      <QuestionFeedbackModal
        isOpen={isFeedbackModalOpen}
        toggle={() => setIsFeedbackModalOpen(false)}
        questionId={currentQuestion?.questionId}
        questionCode={currentQuestion?.questionCode}
        passageId={currentQuestion?.passageId}
        testId={attemptId}
        layoutMode="pmr"
        source="exam"
      />

    </>
  );
};

export default TakeFullLengthExam;
